import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Card,
    Typography,
    Grid,
    Avatar,
    Button,
    styled,
    useTheme,
    useMediaQuery,
    Fab,
    Zoom,
    Tooltip,
    Stack,
    IconButton
} from "@mui/material";

/* ✅ Icons */
import { AiFillBulb, AiOutlineRead } from "react-icons/ai";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import StopIcon from "@mui/icons-material/Stop";

/* ✅ Assets */
const bgImage = "https://images.unsplash.com/photo-1516116216624-53e697fedbea";

/* ✅ Styled Components - Premium Dark Glass Theme */
const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.9), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
    [theme.breakpoints.down("sm")]: {
        paddingTop: theme.spacing(8),
    },
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(16,7,32,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "25px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "white",
}));

const CodeBlock = styled("pre")(({ theme }) => ({
    backgroundColor: "#0d1117",
    color: "#c9d1d9",
    padding: "20px",
    borderRadius: "10px",
    overflowX: "auto",
    fontSize: "0.9rem",
    lineHeight: 1.6,
    border: "1px solid rgba(255,255,255,0.05)",
    fontFamily: "'Fira Code', 'Courier New', monospace",
}));

const ActionButton = styled(Button)(({ theme }) => ({
    backgroundColor: "goldenrod",
    color: "#100720",
    fontWeight: 800,
    padding: "12px 40px",
    borderRadius: "999px",
    transition: "0.3s",
    "&:hover": {
        backgroundColor: "#e0b200",
        transform: "scale(1.05)",
    },
}));

/**
 * TypeScriptTypeAliasesUnions Component
 * * Explains type aliases, union types, and intersection types.
 * Fixed parsing error on line 192.
 */
export default function TypeScriptTypeAliasesUnions() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    /* ✅ UI State */
    const [showScroll, setShowScroll] = useState(false);
    const [copiedIndex, setCopiedIndex] = useState(null);
    const [isSpeaking, setIsSpeaking] = useState(false);

    /* ✅ Scroll Detection for FAB */
    useEffect(() => {
        const checkScroll = () => setShowScroll(window.pageYOffset > 400);
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    /* ✅ Audio/Narration Control */
    const handleSpeak = (text) => {
        window.speechSynthesis.cancel();
        if (isSpeaking) {
            setIsSpeaking(false);
            return;
        }
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.rate = 0.95;
        utterance.onstart = () => setIsSpeaking(true);
        utterance.onend = () => setIsSpeaking(false);
        window.speechSynthesis.speak(utterance);
    };

    const stopSpeech = () => {
        window.speechSynthesis.cancel();
        setIsSpeaking(false);
    };

    /* ✅ Clipboard Utility */
    const handleCopy = (text, index) => {
        navigator.clipboard.writeText(text);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    /* ✅ Modeling Data */
    const lessonModules = useMemo(() => [
        {
            title: "1. Type Aliases",
            tag: "Custom Naming",
            desc: "The 'type' keyword allows you to create a custom name for any type structure, making code readable and reusable.",
            code: `type Point = {\n  x: number;\n  y: number;\n};\n\nlet position: Point = { x: 5, y: 10 };`
        },
        {
            title: "2. Union Types",
            tag: "Logical OR",
            desc: "Unions allow a variable to hold values of multiple different types or specific literal values using the pipe (|) operator.",
            code: `type Status = "loading" | "success" | "error";\nlet currentStatus: Status = "loading";`
        },
        {
            title: "3. Intersection Types",
            tag: "Logical AND",
            desc: "Intersections combine multiple types into one using the ampersand (&) operator. All properties are required.",
            code: `type Person = { name: string };\ntype Worker = { job: string };\ntype Employee = Person & Worker;\n\nconst e: Employee = { name: "Hazzan", job: "Developer" };`
        }
    ], []);

    return (
        <PageWrapper>
            {/* 🏆 Header Section */}
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography
                    variant={isMobile ? "h4" : "h3"}
                    sx={{
                        fontWeight: 800,
                        backgroundColor: "goldenrod",
                        color: "#100720",
                        px: { xs: 3, md: 6 },
                        py: 1.5,
                        borderRadius: "999px",
                        display: "inline-block",
                        boxShadow: "0 6px 20px rgba(218, 165, 32, 0.3)",
                    }}
                >
                    TYPESCRIPT FUNDAMENTALS
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                <Grid item xs={12}>
                    <SectionCard>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography variant="h5" fontWeight={800} color="goldenrod">
                                Aliases & Unions
                            </Typography>
                            <IconButton
                                onClick={() => handleSpeak("Type aliases and unions allow you to model flexible types.")}
                                sx={{ color: "goldenrod" }}
                            >
                                <VolumeUpIcon />
                            </IconButton>
                        </Stack>
                        <Typography variant="body1" sx={{ opacity: 0.9, lineHeight: 1.7 }}>
                            While Interfaces are great for object shapes, <code>type</code> aliases offer unmatched flexibility for defining Unions and Intersections.
                        </Typography>
                    </SectionCard>
                </Grid>

                {lessonModules.map((item, i) => (
                    <Grid item xs={12} key={i}>
                        <SectionCard>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    <Avatar sx={{ bgcolor: "rgba(218, 165, 32, 0.2)", color: "goldenrod" }}>
                                        <AiFillBulb size={24} />
                                    </Avatar>
                                    <Box>
                                        <Typography variant="h6" fontWeight={700}>{item.title}</Typography>
                                        <Typography variant="caption" color="goldenrod" fontWeight={800}>{item.tag}</Typography>
                                    </Box>
                                </Box>

                                <Stack direction="row" spacing={1}>
                                    <Tooltip title="Listen">
                                        <IconButton onClick={() => handleSpeak(`${item.title}. ${item.desc}`)} sx={{ color: "goldenrod" }}>
                                            <VolumeUpIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title={copiedIndex === i ? "Copied!" : "Copy Code"}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => handleCopy(item.code, i)}
                                            sx={{ color: "white", borderColor: "rgba(255,255,255,0.3)" }}
                                        >
                                            {copiedIndex === i ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                        </Button>
                                    </Tooltip>
                                </Stack>
                            </Box>
                            <Typography variant="body1" sx={{ mb: 3, opacity: 0.9 }}>{item.desc}</Typography>
                            <CodeBlock>{item.code}</CodeBlock>
                        </SectionCard>
                    </Grid>
                ))}

                <Grid item xs={12} textAlign="center" sx={{ mt: 4, mb: 10 }}>
                    <Link to="/typescript-dashboard" style={{ textDecoration: "none" }}>
                        <ActionButton variant="contained">NEXT: ENUMS</ActionButton>
                    </Link>
                </Grid>
            </Grid>

            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                {isSpeaking && (
                    <Zoom in={isSpeaking}>
                        <Fab size="medium" color="error" onClick={stopSpeech}>
                            <StopIcon />
                        </Fab>
                    </Zoom>
                )}
                <Zoom in={showScroll}>
                    <Fab
                        size="small"
                        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                        sx={{ bgcolor: "goldenrod" }}
                    >
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </PageWrapper>
    );
}
